import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import { TimeToCompleteItem } from 'design-system/TimeToCompleteCard';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import useAddAccount from 'hooks/useAddAccount';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectUserTierMaxPotInterest } from 'features/pots/reducers/selectors';
import { tierThemes } from 'features/premium/benefits';
import { Tier } from 'features/premium/entities';
import { selectUserTier } from 'features/premium/selectors/tier';
import { selectHasCurrentRentReporting, selectHasRentReporting, } from 'features/rent-reporting/selectors';
import { selectHasSetBudget, selectIsGBUser } from 'reducers/selectors';
const strings = {
    makeTheMost: 'Make the most out of a free upgrade!',
    illDoItLater: 'I’ll do it later',
};
const DeleteReverseTrialSetupScreenScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    useLayoutEffect(() => {
        const goBack = () => {
            navigation.pop();
        };
        navigation.setOptions({
            headerLeft: () => (<NavigationCloseButton onPress={goBack} colorKey="primary"/>),
            headerBackVisible: false,
        });
    }, []);
    const onPressLater = useCallback(() => {
        navigation.pop();
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const tier = useAppSelector(selectUserTier);
    const interestRate = useAppSelector(selectUserTierMaxPotInterest);
    const hasCurrentRentReporting = useAppSelector(selectHasCurrentRentReporting);
    const isRentReportingAvailable = useAppSelector(selectHasRentReporting);
    const hasSetBudget = useAppSelector(selectHasSetBudget);
    const isGb = useAppSelector(selectIsGBUser);
    const addAccount = useAddAccount();
    const { navigateToTab } = useTabNavigation();
    const CHOICES = useMemo(() => [
        {
            estimatedTimeToComplete: '1 minute',
            title: 'Add another bank account',
            description: 'Connect all your bank accounts to see accurate net worth, track all your recurring payments and more...',
            onPress: addAccount,
        },
        {
            estimatedTimeToComplete: '< 3 minutes',
            title: 'Create a budget',
            description: 'Stay on track with your spending!',
            onPress: () => hasSetBudget
                ? navigation.navigateDeprecated('EditBudget', {
                    flow: 'Edit',
                })
                : navigation.navigateDeprecated('SetUpBudget', {}),
        },
        ...(isGb
            ? [
                {
                    estimatedTimeToComplete: '< 5 minutes',
                    title: 'Open savings pot',
                    description: `Open a pot and start earning ${interestRate}% interest`,
                    onPress: () => {
                        navigateToTab('Save');
                    },
                },
            ]
            : []),
        ...(isRentReportingAvailable && !hasCurrentRentReporting
            ? [
                {
                    estimatedTimeToComplete: '< 5 minutes',
                    title: 'Improve your credit history',
                    description: 'Report your rent payments and improve your credit history!',
                    onPress: () => {
                        navigation.navigateDeprecated('RentReportingOnboarding');
                    },
                },
            ]
            : []),
    ], [
        addAccount,
        hasCurrentRentReporting,
        hasSetBudget,
        interestRate,
        isGb,
        isRentReportingAvailable,
        navigateToTab,
    ]);
    return (<AppView withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingBottom}>
            <ScreenInfoHeader title={strings.makeTheMost} subTitle={`${tier === Tier.ultimate
            ? 'You now have access to our higher interest rate and lowest fees. '
            : ''} Start your journey today with this free ${tierThemes[tier].name} upgrade.`} smallSubTitle/>
            <StatusBar barStyle="dark-content" animated/>
            <CardView noMarginBottom>
              {CHOICES.map((choice) => (<TimeToCompleteItem {...choice} key={choice.title}/>))}
            </CardView>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button title={strings.illDoItLater} brand onPress={onPressLater} testID="laterButton"/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default DeleteReverseTrialSetupScreenScreen;
